import React, { useState, useEffect, Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import timeouts from '../../../../constants/timeouts';
import Navigate from '../../../../util/Navigate';
import withRouter from '../../../../util/withRouter';
import Danger from '../../component/alerts/Danger';
import Loading from '../../component/alerts/Loading';
import Footer from '../../component/layout/footer';
import Header from '../../component/layout/header';
import PageHeader from '../../component/layout/pageheader';
import SocialMedia from '../../component/section/socialmedia';
import Glitch from 'glitch-javascript-sdk';

const PublisherOnboardingMultiStepPage = (props) => {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        username: '',
        password: '',
        confirm_password: ''
    });
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [emailOptIn, setEmailOptIn] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [isBrowser, setIsBrowser] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [isPasswordFocused, setIsPasswordFocused] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [emailVerified, setEmailVerified] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        setIsBrowser(typeof window !== 'undefined');

        if (typeof window !== 'undefined') {
            setTimeout(() => {
                const params = new Proxy(new URLSearchParams(window.location.search), {
                    get: (searchParams, prop) => searchParams.get(prop),
                });
                let iscohost = params.iscohost;
                if (iscohost && Glitch.util.Session.isLoggedIn()) {
                    goToNextScreen();
                }
            }, 1000);
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }

        // Calculate password strength
        if (name === 'password') {
            calculatePasswordStrength(value);
        }
    };

    const calculatePasswordStrength = (password) => {
        let strength = 0;
        
        if (password.length >= 8) strength += 1;
        if (password.length >= 12) strength += 1;
        if (/[A-Z]/.test(password)) strength += 1;
        if (/[0-9]/.test(password)) strength += 1;
        if (/[^A-Za-z0-9]/.test(password)) strength += 1;
        
        setPasswordStrength(Math.min(strength, 5));
    };

    const getPasswordStrengthColor = () => {
        if (passwordStrength <= 1) return 'bg-danger';
        if (passwordStrength <= 3) return 'bg-warning';
        return 'bg-success';
    };

    const validateStep = (step) => {
        const newErrors = {};
        
        if (step === 1) {
            if (!formData.email.trim()) {
                newErrors.email = ['Email is required'];
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                newErrors.email = ['Please enter a valid email address'];
            }
        } else if (step === 2) {
            if (!formData.first_name.trim()) newErrors.first_name = ['First name is required'];
            if (!formData.last_name.trim()) newErrors.last_name = ['Last name is required'];
            if (!formData.username.trim()) newErrors.username = ['Username is required'];
        } else if (step === 3) {
            if (!formData.password) {
                newErrors.password = ['Password is required'];
            } else if (formData.password.length < 8) {
                newErrors.password = ['Password must be at least 8 characters'];
            }
            
            if (formData.password !== formData.confirm_password) {
                newErrors.confirm_password = ['Passwords do not match'];
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            
            // Scroll to first error
            const firstError = Object.keys(newErrors)[0];
            document.getElementById(firstError)?.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
            });
            
            return false;
        }

        return true;
    };

    const nextStep = () => {
        if (validateStep(currentStep)) {
            if (currentStep === 1) {
                setEmailVerified(true);
            }
            setCurrentStep(prev => prev + 1);
        }
    };

    const prevStep = () => {
        setCurrentStep(prev => prev - 1);
    };

    const register = (event) => {
        event.preventDefault();

        if (!termsAccepted) {
            setErrors({ terms: ['You must agree to the terms of service'] });
            return;
        }

        const data = { 
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
            email: formData.email.trim(),
            username: formData.username.trim(),
            password: formData.password,
            email_opt_in: emailOptIn
        };

        setIsLoading(true);

        Glitch.api.Auth.register(data).then((response) => {
            Glitch.util.Storage.setAuthToken(response.data.data.token.access_token);
            Glitch.util.Storage.set('user_id', response.data.data.id);
            Glitch.util.Session.processAuthentication(response.data.data);
            setIsLoading(false);
            goToNextScreen();
        }).catch((error) => {
            setIsLoading(false);
            if (error.response && error.response.data) {
                setErrors(error.response.data);
                
                setTimeout(() => {
                    setErrors({});
                }, timeouts.error_message_timeout);
            }
        });
    };

    const goToNextScreen = () => {
        if (typeof window !== "undefined") {
            const params = new URLSearchParams(window.location.search);
            const redirect = params.get('redirect');
            let nextPageUrl = Navigate.publishersOnboardingStep2Page();
            if (redirect) {
                nextPageUrl += `?redirect=${encodeURIComponent(redirect)}`;
            }
            navigate(nextPageUrl);
        }
    };

    const renderStepIndicator = () => {
        return (
            <div className="step-indicator mb-5">
                <div className="d-flex justify-content-between align-items-center">
                    {[1, 2, 3, 4].map((step) => (
                        <Fragment key={step}>
                            <div 
                                className={`step-circle ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
                                onClick={() => currentStep > step && setCurrentStep(step)}
                            >
                                {step}
                                {currentStep > step && (
                                    <i className="fas fa-check ms-1"></i>
                                )}
                            </div>
                            {step < 4 && (
                                <div className={`step-line ${currentStep > step ? 'completed' : ''}`}></div>
                            )}
                        </Fragment>
                    ))}
                </div>
                <div className="d-flex justify-content-between mt-2">
                    <div className={`step-label ${currentStep === 1 ? 'active' : ''}`}>Email</div>
                    <div className={`step-label ${currentStep === 2 ? 'active' : ''}`}>Basic Info</div>
                    <div className={`step-label ${currentStep === 3 ? 'active' : ''}`}>Password</div>
                    <div className={`step-label ${currentStep === 4 ? 'active' : ''}`}>Review</div>
                </div>
            </div>
        );
    };

    const renderStep1 = () => {
        return (
            <div className="step-content">
                <div className="form-group">
                    <label className="form-label">Email Address *</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="your@email.com"
                    />
                    {errors.email && errors.email.map((error, index) => (
                        <Danger message={error} key={index} />
                    ))}
                </div>

                <div className="form-group mt-4">
                    <button 
                        type="button" 
                        className="d-block default-button w-100"
                        onClick={nextStep}
                    >
                        <span>Continue</span>
                    </button>
                </div>

                <div className="text-center mt-3">
                    <span className="d-block cate">
                        Already have an account? <Link to={Navigate.authLogin()} className="text-primary">Log in</Link>
                    </span>
                </div>
            </div>
        );
    };

    const renderStep2 = () => {
        return (
            <div className="step-content">
                <div className="row">
                    <div className="col-md-6">
                        <div className={`form-group ${errors.first_name ? 'has-error' : ''}`}>
                            <label className="form-label">First Name *</label>
                            <input
                                type="text"
                                id="first_name"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="First Name"
                            />
                            {errors.first_name && errors.first_name.map((error, index) => (
                                <Danger message={error} key={index} />
                            ))}
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className={`form-group ${errors.last_name ? 'has-error' : ''}`}>
                            <label className="form-label">Last Name *</label>
                            <input
                                type="text"
                                id="last_name"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Last Name"
                            />
                            {errors.last_name && errors.last_name.map((error, index) => (
                                <Danger message={error} key={index} />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="form-group">
                    <label className="form-label">Username *</label>
                    <input
                        type="text"
                        id="username"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Choose a username"
                    />
                    <small className="form-text text-white">This will be your public profile name</small>
                    {errors.username && errors.username.map((error, index) => (
                        <Danger message={error} key={index} />
                    ))}
                </div>

                <div className="d-flex justify-content-between mt-4">
                    <button 
                        type="button" 
                        className="btn btn-outline-light"
                        onClick={prevStep}
                    >
                        Back
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-primary"
                        onClick={nextStep}
                    >
                        Continue
                    </button>
                </div>
            </div>
        );
    };

    const renderStep3 = () => {
        return (
            <div className="step-content">
                <div className="form-group">
                    <label className="form-label">Password *</label>
                    <input
                        type="password"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Create a password"
                        onFocus={() => setIsPasswordFocused(true)}
                        onBlur={() => setIsPasswordFocused(false)}
                    />
                    {isPasswordFocused && (
                        <div className="password-strength mt-2">
                            <div className="progress" style={{ height: '5px' }}>
                                <div 
                                    className={`progress-bar ${getPasswordStrengthColor()}`} 
                                    role="progressbar" 
                                    style={{ width: `${passwordStrength * 20}%` }}
                                    aria-valuenow={passwordStrength * 20}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ></div>
                            </div>
                            <small className="text-white">
                                {passwordStrength <= 1 && 'Weak'}
                                {passwordStrength === 2 && 'Fair'}
                                {passwordStrength === 3 && 'Good'}
                                {passwordStrength >= 4 && 'Strong'}
                            </small>
                        </div>
                    )}
                    <small className="form-text text-white">
                        Use at least 8 characters with a mix of letters, numbers & symbols
                    </small>
                    {errors.password && errors.password.map((error, index) => (
                        <Danger message={error} key={index} />
                    ))}
                </div>

                <div className="form-group">
                    <label className="form-label">Confirm Password *</label>
                    <input
                        type="password"
                        id="confirm_password"
                        name="confirm_password"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Re-enter your password"
                    />
                    {errors.confirm_password && errors.confirm_password.map((error, index) => (
                        <Danger message={error} key={index} />
                    ))}
                </div>

                <div className="d-flex justify-content-between mt-4">
                    <button 
                        type="button" 
                        className="btn btn-outline-light"
                        onClick={prevStep}
                    >
                        Back
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-primary"
                        onClick={nextStep}
                    >
                        Continue
                    </button>
                </div>
            </div>
        );
    };

    const renderStep4 = () => {
        return (
            <div className="step-content">
                <div className="review-section">
                    <h5 className="mb-4">Review Your Information</h5>
                    
                    <div className="form-group">
                        <label className="form-label">Email Address *</label>
                        <input
                            type="email"
                            id="review_email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="your@email.com"
                        />
                        {errors.email && errors.email.map((error, index) => (
                            <Danger message={error} key={index} />
                        ))}
                    </div>

                    <div className="row mt-3">
                        <div className="col-md-6">
                            <div className={`form-group ${errors.first_name ? 'has-error' : ''}`}>
                                <label className="form-label">First Name *</label>
                                <input
                                    type="text"
                                    id="review_first_name"
                                    name="first_name"
                                    value={formData.first_name}
                                    onChange={handleChange}
                                    className="form-control"
                                    placeholder="First Name"
                                />
                                {errors.first_name && errors.first_name.map((error, index) => (
                                    <Danger message={error} key={index} />
                                ))}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className={`form-group ${errors.last_name ? 'has-error' : ''}`}>
                                <label className="form-label">Last Name *</label>
                                <input
                                    type="text"
                                    id="review_last_name"
                                    name="last_name"
                                    value={formData.last_name}
                                    onChange={handleChange}
                                    className="form-control"
                                    placeholder="Last Name"
                                />
                                {errors.last_name && errors.last_name.map((error, index) => (
                                    <Danger message={error} key={index} />
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="form-group mt-3">
                        <label className="form-label">Username *</label>
                        <input
                            type="text"
                            id="review_username"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Choose a username"
                        />
                        <small className="form-text text-white">This will be your public profile name</small>
                        {errors.username && errors.username.map((error, index) => (
                            <Danger message={error} key={index} />
                        ))}
                    </div>

                    <div className="form-group mt-3">
                        <label className="form-label">Password *</label>
                        <input
                            type="password"
                            id="review_password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Create a password"
                            onFocus={() => setIsPasswordFocused(true)}
                            onBlur={() => setIsPasswordFocused(false)}
                        />
                        {isPasswordFocused && (
                            <div className="password-strength mt-2">
                                <div className="progress" style={{ height: '5px' }}>
                                    <div 
                                        className={`progress-bar ${getPasswordStrengthColor()}`} 
                                        role="progressbar" 
                                        style={{ width: `${passwordStrength * 20}%` }}
                                        aria-valuenow={passwordStrength * 20}
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                    ></div>
                                </div>
                                <small className="text-white">
                                    {passwordStrength <= 1 && 'Weak'}
                                    {passwordStrength === 2 && 'Fair'}
                                    {passwordStrength === 3 && 'Good'}
                                    {passwordStrength >= 4 && 'Strong'}
                                </small>
                            </div>
                        )}
                        {errors.password && errors.password.map((error, index) => (
                            <Danger message={error} key={index} />
                        ))}
                    </div>

                    <div className="form-group mt-3">
                        <label className="form-label">Confirm Password *</label>
                        <input
                            type="password"
                            id="review_confirm_password"
                            name="confirm_password"
                            value={formData.confirm_password}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Re-enter your password"
                        />
                        {errors.confirm_password && errors.confirm_password.map((error, index) => (
                            <Danger message={error} key={index} />
                        ))}
                    </div>

                    <div className="form-group-1 mt-4 mb-3">
                        <div className="form-check">
                            <input
                                type="checkbox"
                                id="emailOptIn"
                                className="form-check-input"
                                checked={emailOptIn}
                                onChange={(e) => setEmailOptIn(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="emailOptIn">
                                Receive occasional product updates, announcements and offers
                            </label>
                        </div>
                    </div>

                    <div className={`form-group-1 mb-4 ${errors.terms ? 'has-error' : ''}`}>
                        <div className="form-check">
                            <input
                                type="checkbox"
                                id="termsAccepted"
                                className="form-check-input"
                                checked={termsAccepted}
                                onChange={(e) => setTermsAccepted(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="termsAccepted">
                                I agree to the <Link to={Navigate.termsPage()} target="_blank" className="text-primary">Terms of Service</Link> and <Link to={Navigate.privacyPage()} target="_blank" className="text-primary">Privacy Policy</Link>
                            </label>
                            {errors.terms && errors.terms.map((error, index) => (
                                <Danger message={error} key={index} />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-between mt-4">
                    <button 
                        type="button" 
                        className="btn btn-outline-light"
                        onClick={prevStep}
                    >
                        Back
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-primary"
                        onClick={register}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <Loading />
                        ) : (
                            <span>Complete Registration</span>
                        )}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <Fragment>
            <div className="login-section padding-top padding-bottom">
                <div className="container">
                    <div className="account-wrapper">
                        {renderStepIndicator()}

                        <h3 className="title">Registration for Studios and Developers</h3>
                        <p className="mb-4">Our AI is tailored specifically for your game and we need some basic information.</p>
                        <hr />

                       

                        {currentStep === 1 && renderStep1()}
                        {currentStep === 2 && renderStep2()}
                        {currentStep === 3 && renderStep3()}
                        {currentStep === 4 && renderStep4()}

                        {currentStep === 1 && isBrowser && (
                            <div className="text-center my-4">
                                <h6 className="mb-3">Register With Social Media</h6>
                                <SocialMedia redirect={`${window.location.origin}${Navigate.publishersOnboardingStep2Page()}`} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Footer />
        </Fragment>
    );
};

export default PublisherOnboardingMultiStepPage;